import { LogOut, User, KeyRound, Menu, Search } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { UserRoleLabels } from "@/types/enums";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import BookingSearchDialog from "@/components/BookingSearchDialog";
import { useCurrentShop } from "@/components/CurrentShopProvider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AppHeaderProps {
  onMenuToggle: () => void;
}

const AppHeader = ({ onMenuToggle }: AppHeaderProps) => {
  const { authUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [staffName, setStaffName] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);

  const {
    areas,
    filteredShops,
    selectedAreaId,
    selectedShopId,
    setSelectedAreaId,
    setSelectedShopId,
    canSwitch,
  } = useCurrentShop();

  const [showBookingSearch, setShowBookingSearch] = useState(false);

  useEffect(() => {
    if (!authUser) return;
    if (authUser.staff_id) {
      supabase
        .from("staff")
        .select("staff_name")
        .eq("id", authUser.staff_id)
        .single()
        .then(({ data }) => {
          if (data) setStaffName(data.staff_name);
        });
    }
    if (authUser.company_id) {
      supabase
        .from("companies")
        .select("company_name")
        .eq("id", authUser.company_id)
        .single()
        .then(({ data }) => {
          if (data) setCompanyName(data.company_name);
        });
    }
  }, [authUser]);

  const handleLogout = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const roleLabel = authUser ? UserRoleLabels[authUser.role] ?? authUser.role : "";
  const displayName = staffName ?? authUser?.email ?? "";
  const orgName = companyName ?? "";

  return (
    <header className="h-14 bg-card border-b border-border flex items-center justify-between px-4 lg:px-6">
      {/* Left: mobile menu + user info */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="lg:hidden p-2 -ml-2 text-foreground hover:bg-muted rounded-md transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="hidden lg:flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <User className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-foreground text-xs">
              {roleLabel}
              {roleLabel ? `（${authUser?.role?.toUpperCase()}）` : ""}
            </p>
            <p className="text-muted-foreground text-xs">
              {orgName ? `${orgName} / ` : ""}
              {displayName}
              {displayName ? " 様" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Right: search, area/shop selectors, actions */}
      <div className="flex items-center gap-3">
        {/* Search icon */}
        <button
          onClick={() => setShowBookingSearch(true)}
          className="w-9 h-9 flex items-center justify-center border border-border rounded-md hover:bg-muted transition-colors"
        >
          <Search className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Area selector（admin/root のみ切替可。shop/user は自店固定） */}
        <Select value={selectedAreaId ?? undefined} onValueChange={setSelectedAreaId} disabled={!canSwitch}>
          <SelectTrigger className="w-auto min-w-[80px] h-9 text-xs border-border">
            <SelectValue placeholder="地域" />
          </SelectTrigger>
          <SelectContent>
            {areas.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.area_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Shop selector */}
        <Select value={selectedShopId ?? undefined} onValueChange={setSelectedShopId} disabled={!canSwitch}>
          <SelectTrigger className="w-auto min-w-[120px] h-9 text-xs border-border">
            <SelectValue placeholder="店舗" />
          </SelectTrigger>
          <SelectContent>
            {filteredShops.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.shop_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-md text-xs text-foreground hover:bg-muted transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">ログアウト</span>
        </button>
      </div>

      <BookingSearchDialog open={showBookingSearch} onOpenChange={setShowBookingSearch} />
    </header>
  );
};

export default AppHeader;
